/**
 * Production environment values, injected at build time.
 *
 * These are the only environment values sourced from `import.meta.env`. They
 * live in a dedicated module so the rest of the environments code stays free of
 * the `import.meta` token — Node's CommonJS script runtime (used by Jest) fails
 * to compile any module that contains it ("Cannot use 'import.meta' outside a
 * module"), which is why the environments code was previously untestable.
 *
 * Vite statically replaces the `import.meta.env.VITE_*` reads below at build
 * time, so production behaviour is unchanged. Jest specs that need the
 * environments module stub this file via `jest.mock`, so the real reads are
 * never evaluated (and never compiled) under the test runtime.
 */

/**
 * The production LaunchDarkly environment.
 *
 * One character apart from the id the seven non-production environments share, so
 * check the last character before assuming a flag is being read from the wrong
 * place.
 */
const PRODUCTION_LAUNCH_DARKLY_CLIENT_ID = '5e4569eb47c8f508323f23f8';

/**
 * The API base URL override, when a deployment sets one.
 *
 * Lives here rather than next to the code that uses it, because this module is
 * the only one allowed to contain the `import.meta` token. `getApiBaseUrl` reads
 * it as a fallback when the current environment has no AppSync URL.
 */
export const API_URL_OVERRIDE: string =
  import.meta.env.VITE_API_URL || process.env.REACT_APP_API_URL || '';

export interface ProductionEnvConfig {
  appSyncUrl: string;
  userPoolId: string;
  identityPoolId: string;
  userPoolWebClientId: string;
  launchDarklyClientId: string;
}

// Read once at module load, mirroring the previous inline `choprd` config.
export const PRODUCTION_ENV_CONFIG: ProductionEnvConfig = {
  appSyncUrl:
    import.meta.env.VITE_GRAPHQL_ENDPOINT ||
    process.env.REACT_APP_GRAPHQL_ENDPOINT ||
    '',
  userPoolId:
    import.meta.env.VITE_USER_POOL_ID ||
    process.env.REACT_APP_USER_POOL_ID ||
    '',
  identityPoolId:
    import.meta.env.VITE_IDENTITY_POOL_ID ||
    process.env.REACT_APP_IDENTITY_POOL_ID ||
    '',
  userPoolWebClientId:
    import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID ||
    process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID ||
    '',
  // Production reads its own LaunchDarkly environment, never the one the other
  // seven share. The id is committed for the same reason the rest of the web apps
  // commit theirs (`frontend/apps/web/.env.production`, `choco-web`): a
  // client-side id is public by design and ships in the bundle either way. The
  // variable still wins if it is set, so this can be overridden per deployment
  // without a code change.
  launchDarklyClientId:
    import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID ||
    PRODUCTION_LAUNCH_DARKLY_CLIENT_ID,
};
